/* eslint-disable no-unsafe-optional-chaining */
import { FormLabel, Input, InputGroup, RadioButton } from "@wfp/ui"
import { BasicOption, FormFetchData, FormFrequencyData } from "interfaces/dataInterfaces"
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react"
import { SingleDatePicker } from "react-dates"
import Select from "react-select"
import { useGetFormsQuery, useGetProjectsQuery, useTestFetchingMutation } from "services/core/dataFetchFromMoDa"
import { useGetApiKeyListQuery } from "services/core/user"
import moment from "moment"
import Button from "components/Shared/Button/Button"
import useCommon from "hooks/useCommon"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import ScheduleFrequency from "./ScheduleFrequency"
import CronInfo from "./components/CronInfo"

type SurveyFormFetchProps = {
  data: FormFetchData
  setData: Dispatch<SetStateAction<FormFetchData>>
  setFetchingMode: Dispatch<SetStateAction<number>>
  onSubmit?: () => void
  currentMode: number
  fetchingMode: number
  editMode?: boolean
  cron?: { id: string; cron: string }[]
  currentApiKey?: BasicOption
  setValidation?: Dispatch<SetStateAction<boolean>>
}

const initialFormFetch: FormFetchData = {
  apiKey: 0,
  formId: 0,
  projectId: 0,
  frequencyId: 0,
  endDate: null,
}

function SurveyFormFetch({
  data: workBookData,
  setData,
  onSubmit,
  currentMode,
  fetchingMode,
  setFetchingMode,
  editMode,
  cron,
  currentApiKey,
  setValidation,
}: SurveyFormFetchProps) {
  const { showSnackBar } = useCommon()
  const [focusedDate, setFocusedDate] = useState(false)
  const [isAPITested, setIsAPITested] = useState(false)
  const [editFrequency, setEditFrequency] = useState(false)

  const { data: apiKeys, isFetching: loadingApiKeys } = useGetApiKeyListQuery({
    dataProviderPlatformId: currentMode,
  })
  const {
    data: projects,
    isFetching: loadingProjects,
    isError: isErrorProjects,
  } = useGetProjectsQuery({ apiKeyId: workBookData.apiKey }, { skip: editMode || !workBookData.apiKey })
  const { data: forms, isFetching: loadingForms } = useGetFormsQuery(
    { apiKeyId: workBookData.apiKey, projectId: workBookData.projectId },
    { skip: editMode || !workBookData.apiKey || !workBookData.projectId },
  )
  const [testFetching, { isLoading: isLoadingTest }] = useTestFetchingMutation()

  const handleChangeDate = (date: moment.Moment | null) => {
    if (!date) return
    setData({ ...workBookData, endDate: date.set({ hour: moment().hour(), minute: moment().minute() }).format() })
  }

  const handleTestFetch = async () => {
    if (!workBookData.apiKey || !workBookData.formId) {
      showSnackBar("API Key and Form are required", "warning")
      return
    }
    try {
      const result = await testFetching({ apiKeyId: workBookData.apiKey, formId: workBookData.formId }).unwrap()
      if (result.statusCode === 200) showSnackBar(result.message)
      if (result.statusCode === 404) showSnackBar(result.message, "warning")
      setIsAPITested(true)
      setValidation && setValidation(true)
    } catch (error: any) {
      setIsAPITested(false)
      setValidation && setValidation(false)
      showSnackBar(error?.data?.message, "error")
    }
  }

  const handleFrequency = (params: FormFrequencyData) => {
    setData({ ...workBookData, ...params })
  }

  const handleFetchingMode = (e: ChangeEvent<HTMLInputElement>) => {
    setFetchingMode(+e.target.value)
    setData({ ...workBookData, frequencyId: 0 })
  }

  const validateForm = () => {
    if (fetchingMode === 1 && workBookData.apiKey && workBookData.projectId && workBookData.formId) {
      return true
    }
    if (
      editMode &&
      fetchingMode === 2 &&
      workBookData.apiKey &&
      workBookData.projectId &&
      workBookData.formId &&
      workBookData.endDate
    ) {
      return true
    }
    return !!(
      fetchingMode === 2 &&
      workBookData.apiKey &&
      workBookData.projectId &&
      workBookData.formId &&
      workBookData.frequencyId &&
      workBookData.endDate
    )
  }

  const handleApiKeys = e => {
    if (editMode) {
      setData({ ...workBookData, apiKey: e.value })
    } else {
      setData({ ...initialFormFetch, apiKey: e.value })
    }
    setValidation && setValidation(false)
  }

  useEffect(() => {
    if (isAPITested) setIsAPITested(false)
  }, [workBookData.apiKey, workBookData.projectId, workBookData.formId])

  return (
    <>
      <div className="grid grid-cols-2 gap-4 mt-2">
        <div>
          <FormLabel htmlFor="keys">API key</FormLabel>
          <Select
            inputId="keys"
            isLoading={loadingApiKeys}
            placeholder="Select API key"
            options={apiKeys ?? []}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.value?.toString() ?? ""}
            value={apiKeys?.find(k => +k?.value === +workBookData.apiKey)}
            onChange={handleApiKeys}
            defaultValue={currentApiKey}
          />
        </div>
        {!editMode && (
          <>
            <div>
              <FormLabel htmlFor="projects">Projects</FormLabel>
              <Select
                inputId="projects"
                isLoading={loadingProjects}
                placeholder="Select project"
                options={projects ?? []}
                getOptionLabel={option => option.name}
                getOptionValue={option => option.projectid?.toString() ?? ""}
                value={projects?.find(p => p?.projectid === workBookData.projectId) ?? null}
                onChange={e => e && setData({ ...workBookData, projectId: Number(e.projectid), formId: 0 })}
                isDisabled={editMode || isErrorProjects || !workBookData.apiKey}
              />
            </div>
            <div>
              <FormLabel htmlFor="forms">Forms</FormLabel>
              <Select
                inputId="forms"
                isLoading={loadingForms}
                placeholder="Select form"
                options={forms ?? []}
                getOptionLabel={option => option.title}
                getOptionValue={option => option.formid?.toString() ?? ""}
                value={forms?.find(f => f?.formid === workBookData.formId) ?? null}
                onChange={e => e && setData({ ...workBookData, formId: e.formid })}
                menuPlacement={editMode ? "bottom" : "top"}
                isDisabled={editMode || !workBookData.projectId}
              />
            </div>
          </>
        )}

        <div className="d-flex items-end justify-end">
          <Button
            action={handleTestFetch}
            label="Test fetching"
            className="btn-full blue"
            icon={isLoadingTest ? faSpinner : undefined}
          />
        </div>
      </div>

      <CronInfo list={cron} />
      {editMode ? (
        <Button
          action={() => setEditFrequency(prev => !prev)}
          label={editFrequency ? "Cancel" : "Changes fetch settings"}
          className={`btn-full mt-1 ${editFrequency ? "" : "blue"}`}
        />
      ) : null}

      {((editMode && editFrequency) || (!editMode && isAPITested)) && (
        <div className="mt-2">
          <InputGroup name="input-group" labelText="Please select your fetching mode">
            <RadioButton
              name="manual"
              value={1}
              labelText="Manual fetching"
              onChange={handleFetchingMode}
              checked={fetchingMode === 1}
            />
            <RadioButton
              name="auto"
              value={2}
              labelText="Scheduled fetching"
              onChange={handleFetchingMode}
              checked={fetchingMode === 2}
            />
          </InputGroup>

          {fetchingMode === 2 && (
            <>
              <hr className="my-2 border-gray-100" />
              <ScheduleFrequency onFrequencyComplete={handleFrequency} />

              <div className="wfp--form-item mt-2">
                <Input labelText="Retrive data until">
                  {() => (
                    <SingleDatePicker
                      displayFormat="DD/MM/YYYY"
                      placeholder="Pick a date"
                      showDefaultInputIcon
                      inputIconPosition="after"
                      date={workBookData.endDate ? moment(workBookData.endDate) : null}
                      onDateChange={date => handleChangeDate(date)}
                      focused={focusedDate}
                      onFocusChange={() => setFocusedDate(prev => !prev)}
                      id="initial-date"
                      openDirection="up"
                    />
                  )}
                </Input>
              </div>
            </>
          )}

          {onSubmit && (
            <div className="d-flex items-end justify-end">
              <Button
                action={() => (validateForm() ? onSubmit() : undefined)}
                label="Save"
                className="btn-full blue"
                style={{ cursor: validateForm() ? "" : "not-allowed" }}
              />
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default SurveyFormFetch
